import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Combobox } from '@cargomatic/ui';
export const DEFAULT_DEBOUNCE_MS = 300;
export const DEFAULT_MIN_QUERY_LENGTH = 2;
export function AsyncCombobox(props) {
    const { loadOptions, itemToLabel, itemToValue, renderItem, debounceMs = DEFAULT_DEBOUNCE_MS, minQueryLength = DEFAULT_MIN_QUERY_LENGTH, initialItems, placeholder, disabled, id, className, popoverClassName, onInputValueChange, onItemsChange, inputValue, emptyMessage, multiple = false, value, onValueChange, } = props;
    const [items, setItemsState] = useState(initialItems !== null && initialItems !== void 0 ? initialItems : []);
    const [isSearching, setIsSearching] = useState(false);
    // Single-select display: base-ui's Combobox keeps its input text in its own
    // internal state and never re-derives it from `value`, so a selected item's
    // label silently blanks on re-render. Drive `inputValue` from `value`,
    // keeping a local draft (null unless typing) so type-to-filter still works.
    const [typeDraft, setTypeDraft] = useState(null);
    const requestSeq = useRef(0);
    const lastQueryRef = useRef('');
    const onItemsChangeRef = useRef(onItemsChange);
    onItemsChangeRef.current = onItemsChange;
    const setItems = useCallback((next) => {
        var _a;
        setItemsState(next);
        (_a = onItemsChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onItemsChangeRef, next);
    }, []);
    useEffect(() => {
        if (lastQueryRef.current.length < minQueryLength) {
            setItems(initialItems !== null && initialItems !== void 0 ? initialItems : []);
        }
    }, [initialItems, minQueryLength, setItems]);
    const handleSearch = useCallback(async (query) => {
        lastQueryRef.current = query;
        const seq = ++requestSeq.current;
        if (!query || query.length < minQueryLength) {
            setItems(initialItems !== null && initialItems !== void 0 ? initialItems : []);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        try {
            const results = await loadOptions(query);
            if (seq === requestSeq.current)
                setItems(results);
        }
        catch (e) {
            console.error(e);
            if (seq === requestSeq.current)
                setItems([]);
        }
        finally {
            if (seq === requestSeq.current)
                setIsSearching(false);
        }
    }, [loadOptions, minQueryLength, initialItems, setItems]);
    // Internal inputValue control only when the consumer doesn't control it and
    // we're single-select (multi renders selection as chips, not input text).
    const manageInputValue = inputValue === undefined && !multiple;
    const selectedLabel = !multiple && value != null && itemToLabel ? itemToLabel(value) : '';
    const handleInputValueChange = useCallback((next) => {
        if (manageInputValue)
            setTypeDraft(next);
        onInputValueChange === null || onInputValueChange === void 0 ? void 0 : onInputValueChange(next);
    }, [manageInputValue, onInputValueChange]);
    const handleValueChange = useCallback((next) => {
        if (manageInputValue)
            setTypeDraft(null);
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(next);
    }, [manageInputValue, onValueChange]);
    const comboboxProps = {
        multiple,
        items,
        isLoading: isSearching,
        onSearch: handleSearch,
        debounceMs,
        itemToLabel,
        itemToValue,
        renderItem,
        placeholder,
        disabled,
        id,
        className,
        popoverClassName,
        onValueChange: handleValueChange,
        onInputValueChange: handleInputValueChange,
    };
    if (value !== undefined)
        comboboxProps.value = value;
    if (inputValue !== undefined)
        comboboxProps.inputValue = inputValue;
    else if (manageInputValue)
        comboboxProps.inputValue = typeDraft != null ? typeDraft : selectedLabel;
    if (emptyMessage !== undefined)
        comboboxProps.emptyMessage = emptyMessage;
    return (React.createElement(Combobox, Object.assign({}, comboboxProps)));
}
